import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';

export interface PieChartCardProps {
  title: string;
  subtitle?: string;
  segments: { label: string; value: number }[];
  emptyMessage?: string;
}

export function PieChartCard({
  title,
  subtitle,
  segments,
  emptyMessage = 'No data',
}: PieChartCardProps) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (!total || !segments.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-app-text-secondary dark:text-gray-400">{emptyMessage}</div>
        </CardContent>
      </Card>
    );
  }
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const cx = 140;
  const cy = 120;
  const r = 70;
  const rInner = 42;
  let acc = 0;
  const paths: { d: string; color: string }[] = [];
  segments.forEach((seg, i) => {
    const p = seg.value / total;
    const a0 = 2 * Math.PI * acc;
    const a1 = 2 * Math.PI * (acc + p);
    acc += p;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const large = p > 0.5 ? 1 : 0;
    const outer = `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    const xi0 = cx + rInner * Math.cos(a0);
    const yi0 = cy + rInner * Math.sin(a0);
    const xi1 = cx + rInner * Math.cos(a1);
    const yi1 = cy + rInner * Math.sin(a1);
    const inner = `M ${cx} ${cy} L ${xi0} ${yi0} A ${rInner} ${rInner} 0 ${large} 1 ${xi1} ${yi1} Z`;
    paths.push({ d: `${outer} ${inner}`, color: colors[i % colors.length] });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {subtitle && <p className="text-sm text-app-text-secondary dark:text-gray-400">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-4">
          <svg width={280} height={240} className="flex-shrink-0">
            {paths.map((p, i) => (
              <path key={i} d={p.d} fill={p.color} stroke="var(--app-bg, #fff)" strokeWidth={2} />
            ))}
          </svg>
          <ul className="text-sm space-y-1">
            {segments.map((seg, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colors[i % colors.length] }} />
                <span className="text-app-text dark:text-gray-200">{seg.label}</span>
                <span className="text-app-text-secondary dark:text-gray-400">({seg.value})</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
